<template>
  <div class="admin-teams container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Teams-Übersicht</li>
      </ol>
    </nav>

    <div class="container-fluid p-4" id="main-content">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 class="mb-0">
            <font-awesome-icon icon="users" class="me-2" />
            Teams-Übersicht
          </h1>
          <p class="text-muted mb-0">
            Alle Teams mit Status und Kontostand verwalten
          </p>
        </div>
        <BackButton />
      </div>

      <!-- Search and Filters -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="input-group">
                <span class="input-group-text">
                  <font-awesome-icon icon="search" />
                </span>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="form-control"
                  placeholder="Bereich-Name oder Beschreibung suchen..."
                  @input="debouncedSearch"
                />
              </div>
            </div>
            <div class="col-md-3">
              <multiselect
                v-model="statusFilter"
                :options="statusOptions"
                :searchable="true"
                :close-on-select="true"
                :use-teleport="true"
                :show-labels="false"
                placeholder="Status auswählen..."
                label="label"
                track-by="value"
                :no-options="'Keine Status verfügbar'"
                :no-results="'Keine Status gefunden'"
                @select="loadTeams"
                @remove="loadTeams"
              />
            </div>
            <div class="col-md-3">
              <multiselect
                v-model="sortField"
                :options="sortOptions"
                :searchable="true"
                :close-on-select="true"
                :use-teleport="true"
                :show-labels="false"
                placeholder="Sortierung wählen..."
                label="label"
                track-by="value"
                :no-options="'Keine Sortierungen verfügbar'"
                :no-results="'Keine Sortierungen gefunden'"
                @select="loadTeams"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Teams Table -->
      <div class="card shadow-sm">
        <div class="card-body p-0">
          <!-- Loading -->
          <div v-if="loading" class="text-center my-5">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Teams werden geladen...</span>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="alert alert-danger m-4" role="alert">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            {{ error }}
          </div>

          <!-- Teams Table -->
          <div v-else-if="teams.length > 0" class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Mitglieder</th>
                  <th>Kontostand</th>
                  <th>Erstellt von</th>
                  <th>Erstellt am</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="team in teams" :key="team.id">
                  <td>
                    <div>
                      <div class="fw-bold">{{ team.name }}</div>
                      <div class="text-muted small" v-if="team.description">
                        {{ team.description }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span :class="getStatusBadgeClass(team.status)" class="badge">
                      {{ getStatusLabel(team.status) }}
                    </span>
                    <div v-if="team.plan && team.endDate" class="small text-muted mt-1">
                      Bis {{ formatDate(team.endDate) }}
                    </div>
                  </td>
                  <td>
                    <span class="badge bg-secondary">
                      {{ team.memberCount }} Mitglieder
                    </span>
                  </td>
                  <td>
                    <span :class="getBalanceClass(team.accountBalance)" class="fw-bold">
                      {{ formatCoins(team.accountBalance) }}
                    </span>
                  </td>
                  <td>
                    <div v-if="team.creator">
                      <div class="fw-bold">{{ team.creator.name }}</div>
                      <div class="text-muted small">{{ team.creator.email }}</div>
                    </div>
                    <span v-else class="text-muted">Unbekannt</span>
                  </td>
                  <td>
                    <span class="text-muted">{{ formatDate(team.createdAt) }}</span>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        class="btn btn-outline-success"
                        @click="viewTeamUsers(team.id)"
                        title="Benutzer des Teams anzeigen"
                      >
                        <font-awesome-icon icon="users" />
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-info"
                        @click="viewTeamBillings(team.id)"
                        title="Rechnungen anzeigen"
                      >
                        <font-awesome-icon icon="receipt" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-5">
            <font-awesome-icon icon="users" size="3x" class="text-muted mb-3" />
            <h5 class="text-muted">Keine Teams gefunden</h5>
            <p class="text-muted">Es wurden keine Teams gefunden, die den Suchkriterien entsprechen.</p>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.totalPages > 1" class="card-footer">
          <nav aria-label="Teams Pagination">
            <ul class="pagination pagination-sm justify-content-center mb-0">
              <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                <button
                  class="page-link"
                  @click="changePage(pagination.currentPage - 1)"
                  :disabled="pagination.currentPage === 1"
                >
                  <font-awesome-icon icon="chevron-left" />
                </button>
              </li>
              
              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === pagination.currentPage }"
              >
                <button class="page-link" @click="changePage(page)">
                  {{ page }}
                </button>
              </li>
              
              <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                <button
                  class="page-link"
                  @click="changePage(pagination.currentPage + 1)"
                  :disabled="pagination.currentPage === pagination.totalPages"
                >
                  <font-awesome-icon icon="chevron-right" />
                </button>
              </li>
            </ul>
          </nav>
          
          <div class="text-center mt-2">
            <small class="text-muted">
              Seite {{ pagination.currentPage }} von {{ pagination.totalPages }} 
              ({{ pagination.totalItems }} Teams insgesamt)
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { adminApi } from '@/api/admin'
import BackButton from '@/components/common/BackButton.vue'
import { debounce } from 'lodash-es'
import Multiselect from 'vue-multiselect'

const router = useRouter()

// Reactive data
const loading = ref(false)
const error = ref(null)
const teams = ref([])
const pagination = ref(null)
const searchQuery = ref('')
const statusFilter = ref(null)
const sortField = ref({ value: 'createdAt', label: 'Erstellt am' })
const sortDirection = ref('DESC')

// Options for multiselects
const statusOptions = [
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'ultimate', label: 'Ultimate' }
]

const sortOptions = [
  { value: 'createdAt', label: 'Erstellt am' },
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'memberCount', label: 'Mitglieder' },
  { value: 'accountBalance', label: 'Kontostand' }
]

// Debounced search
const debouncedSearch = debounce(() => {
  loadTeams()
}, 500)

// Computed properties
const visiblePages = computed(() => {
  if (!pagination.value) return []
  
  const current = pagination.value.currentPage
  const total = pagination.value.totalPages
  const delta = 2
  
  let start = Math.max(1, current - delta)
  let end = Math.min(total, current + delta)
  
  // Adjust if we're near the beginning or end
  if (current <= delta) {
    end = Math.min(total, 2 * delta + 1)
  }
  if (current >= total - delta) {
    start = Math.max(1, total - 2 * delta)
  }
  
  const pages = []
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Methods
const loadTeams = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = {
      page: pagination.value?.currentPage || 1,
      limit: 20,
      search: searchQuery.value,
      status: statusFilter.value?.value || '',
      sortField: sortField.value?.value || 'createdAt',
      sortDirection: sortDirection.value
    }
    
    const response = await adminApi.getAllTeams(params)
    teams.value = response.teams
    pagination.value = response.pagination
  } catch (err) {
    console.error('Error loading teams:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Teams'
  } finally {
    loading.value = false
  }
}

const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    pagination.value.currentPage = page
    loadTeams()
  }
}

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pro':
      return 'bg-primary'
    case 'ultimate':
      return 'bg-success'
    case 'free':
    default:
      return 'bg-secondary'
  }
}

const getStatusLabel = (status) => {
  switch (status) {
    case 'pro':
      return 'Pro'
    case 'ultimate':
      return 'Ultimate'
    case 'free':
    default:
      return 'Free'
  }
}

const getBalanceClass = (balance) => {
  if (balance > 1000) return 'text-success'
  if (balance > 100) return 'text-warning'
  return 'text-danger'
}

const formatCoins = (coins) => {
  return new Intl.NumberFormat('de-DE').format(coins || 0)
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const viewTeamBillings = (teamId) => {
  // Navigate to billings with team filter
  router.push(`/admin/billings?teamId=${teamId}`)
}

const viewTeamUsers = (teamId) => {
  // Navigate to users with team filter
  router.push(`/admin/users?teamId=${teamId}`)
}

// Watchers
watch([statusFilter, sortField], () => {
  loadTeams()
})

// Lifecycle
onMounted(() => {
  loadTeams()
})
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped>
.admin-teams {
  min-height: 100vh;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.75em;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.pagination .page-link {
  color: #007bff;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
}

.card-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .btn-group-sm .btn {
    padding: 0.125rem 0.25rem;
    font-size: 0.75rem;
  }
}
</style>
